/**
 * CaptureScreenModal — "Capture screen" on Upload Docs.
 *
 * `grabScreenFrame()` asks the browser to share a tab / window / screen (the
 * browser's own picker — a web page can never capture without it), takes ONE
 * frame and stops sharing at once. The modal then shows that frame; the user
 * drags a box over the part to keep (or keeps the whole picture) and it is
 * handed back as a PNG file to the normal upload queue.
 *
 * Must be started from a click (browsers allow screen sharing only then).
 * Not available on iPhone / iPad — `canCaptureScreen()` hides the button there.
 *
 * Keyboard: Enter uploads, Esc closes.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import './UploadDocsPage.css';

export function canCaptureScreen(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices
    && typeof (navigator.mediaDevices as any).getDisplayMedia === 'function';
}

/** One frame of what the user picks to share, or null if they cancelled. */
export async function grabScreenFrame(): Promise<HTMLCanvasElement | null> {
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: false,
      // Chrome: leave this Akountant tab out of the list — capturing it is never wanted.
      selfBrowserSurface: 'exclude',
      surfaceSwitching: 'exclude',
      monitorTypeSurfaces: 'include',
    } as any);
  } catch {
    return null;   // the user cancelled the browser's picker (or it was refused)
  }
  try {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    await video.play();
    // The first frame can be blank — give the stream a moment to deliver a real one.
    await new Promise(resolve => setTimeout(resolve, 400));
    if (!video.videoWidth || !video.videoHeight) return null;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    video.srcObject = null;
    return canvas;
  } finally {
    stream.getTracks().forEach(t => t.stop());   // sharing ends right after the one frame
  }
}

interface Box { x: number; y: number; w: number; h: number; }   // in displayed-image pixels

interface CaptureScreenModalProps {
  frame: HTMLCanvasElement;
  onUse: (file: Blob) => void;
  onRetake: () => void;
  onClose: () => void;
}

const MIN_BOX = 8;

const CaptureScreenModal: React.FC<CaptureScreenModalProps> = ({ frame, onUse, onRetake, onClose }) => {
  const [src] = useState(() => frame.toDataURL('image/png'));
  const imgRef = useRef<HTMLImageElement>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const useBtnRef = useRef<HTMLButtonElement>(null);
  const [box, setBox] = useState<Box | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { useBtnRef.current?.focus(); }, []);

  const pointAt = (e: React.PointerEvent) => {
    const r = imgRef.current!.getBoundingClientRect();
    return {
      x: Math.min(Math.max(e.clientX - r.left, 0), r.width),
      y: Math.min(Math.max(e.clientY - r.top, 0), r.height),
    };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    startRef.current = pointAt(e);
    setBox(null);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const s = startRef.current;
    if (!s) return;
    const p = pointAt(e);
    setBox({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) });
  };
  const onPointerUp = () => {
    startRef.current = null;
    setBox(b => (b && b.w >= MIN_BOX && b.h >= MIN_BOX ? b : null));
  };

  const upload = useCallback(() => {
    const img = imgRef.current;
    if (!img || busy) return;
    setBusy(true);
    // Displayed → real pixels (the frame is shown scaled down to fit).
    const scale = frame.width / img.clientWidth;
    const sel = box
      ? { x: Math.round(box.x * scale), y: Math.round(box.y * scale), w: Math.round(box.w * scale), h: Math.round(box.h * scale) }
      : { x: 0, y: 0, w: frame.width, h: frame.height };
    const out = document.createElement('canvas');
    out.width = sel.w;
    out.height = sel.h;
    out.getContext('2d')?.drawImage(frame, sel.x, sel.y, sel.w, sel.h, 0, 0, sel.w, sel.h);
    out.toBlob(blob => {
      out.width = 0; out.height = 0;
      setBusy(false);
      if (blob) onUse(blob);
    }, 'image/png');
  }, [box, busy, frame, onUse]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      // Enter on a focused button presses that button (Retake / Cancel), not Upload.
      else if (e.key === 'Enter' && !(e.target instanceof HTMLButtonElement)) { e.preventDefault(); upload(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, upload]);

  return (
    <div className="ud-split-overlay" onClick={onClose}>
      <div className="ud-split-modal ud-capture-modal" role="dialog" aria-modal="true" aria-label="Capture screen" onClick={e => e.stopPropagation()}>
        <div className="ud-split-header">
          <div>
            <h3 className="ud-split-title">Capture screen</h3>
            <p className="ud-split-sub">
              Drag a box over the part you want to keep — or upload the whole picture.
            </p>
          </div>
          <button type="button" className="ud-paste-toast-close" aria-label="Close" onClick={onClose}>×</button>
        </div>

        <div className="ud-capture-body">
          <div
            className="ud-capture-stage"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <img ref={imgRef} src={src} alt="Captured screen" draggable={false} />
            {box && (
              <div className="ud-capture-box" style={{ left: box.x, top: box.y, width: box.w, height: box.h }} />
            )}
          </div>
        </div>

        <div className="ud-split-footer">
          <span className="ud-capture-size">
            {box ? 'Selected area will be uploaded' : 'Whole picture will be uploaded'}
          </span>
          <div className="ud-capture-actions">
            {box && <button type="button" className="ud-btn ud-btn-secondary" onClick={() => setBox(null)}>Clear selection</button>}
            <button type="button" className="ud-btn ud-btn-secondary" onClick={onRetake}>Retake</button>
            <button type="button" className="ud-btn ud-btn-secondary" onClick={onClose}>Cancel</button>
            <button ref={useBtnRef} type="button" className="ud-btn ud-btn-primary" onClick={upload} disabled={busy}>
              {box ? 'Upload selection' : 'Upload whole picture'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaptureScreenModal;
